import { isRejectedWithValue } from '@reduxjs/toolkit';
import type { MiddlewareAPI, Middleware } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';

/**
 * Global error handling middleware for RTK Query and other rejected actions.
 * Captures API failures and network errors, displaying them as user-friendly toasts.
 */
export const rtkQueryErrorLogger: Middleware =
  (_api: MiddlewareAPI) => (next) => (action) => {
    // RTK Query uses `isRejectedWithValue` for API errors
    if (isRejectedWithValue(action)) {
      console.warn('Caught an RTK Query error:', action);

      const payload: any = action.payload;
      let errorMessage = 'An unexpected error occurred';

      if (payload) {
        // Handle RTK Query standard error format
        if (payload.data && typeof payload.data === 'object') {
          errorMessage = payload.data.message || payload.data.error || JSON.stringify(payload.data);
        } else if (payload.error) {
          errorMessage = payload.error;
        } else if (payload.status === 'FETCH_ERROR') {
          errorMessage = 'Network error: Unable to connect to the server. Please check your internet connection.';
        } else if (payload.status === 'PARSING_ERROR') {
          errorMessage = 'Data parsing error: Received invalid response from server.';
        } else if (payload.status === 401) {
          errorMessage = 'Session expired. Please log in again.';
        } else if (payload.status === 403) {
          errorMessage = 'You do not have permission to perform this action.';
        } else if (payload.status === 404) {
          errorMessage = 'Requested resource not found.';
        } else if (payload.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }
      }

      // Show the error toast
      toast.error(errorMessage, {
        id: `api-error-${errorMessage.substring(0, 20)}`, // Prevent duplicate toasts for same error
        duration: 5000,
      });
    }

    return next(action);
  };
